import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const TermsOfUse = () => {
  const [activeSection, setActiveSection] = useState('');
  const navigate = useNavigate();

  const sections = [
    { id: 'acceptance', title: 'Acceptance of Terms', icon: '✅' },
    { id: 'use-application', title: 'Use of the Application', icon: '⚙️' },
    { id: 'account', title: 'Account Registration', icon: '👤' },
    { id: 'contact', title: 'Contact Information', icon: '📞' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200;
      
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const elementTop = element.offsetTop;
          const elementBottom = elementTop + element.offsetHeight;
          
          if (scrollPosition >= elementTop && scrollPosition < elementBottom) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <Navbar />

      <div className="pt-20 sm:pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
            {/* Table of Contents */}
            <div className="lg:w-64 lg:flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
                    <span className="mr-2">📋</span>
                    <span className="hidden sm:inline">Table of Contents</span>
                    <span className="sm:hidden">Contents</span>
                  </h3>
                  <nav className="grid grid-cols-2 lg:grid-cols-1 gap-2 lg:space-y-0">
                    {sections.map((section) => (
                      <button
                        key={section.id}
                        onClick={() => scrollToSection(section.id)}
                        className={`w-full text-left px-2 sm:px-3 py-2 rounded-lg text-xs sm:text-sm transition-all duration-200 flex items-center ${
                          activeSection === section.id
                            ? 'bg-blue-100 text-blue-700 font-medium border-l-2 lg:border-l-4 border-blue-500'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                      >
                        <span className="mr-1 sm:mr-2 text-sm">{section.icon}</span>
                        <span className="truncate">{section.title}</span>
                      </button>
                    ))}
                  </nav>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 space-y-6 lg:space-y-8">
              {/* Header Section */}
              <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 shadow-xl border border-gray-200 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-full -translate-y-20 translate-x-20"></div>
                <div className="relative">
                  <div className="flex items-start sm:items-center space-x-3 sm:space-x-4 mb-4 sm:mb-6">
                    <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-2 sm:p-3 rounded-lg sm:rounded-xl flex-shrink-0">
                      <svg className="w-5 h-5 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div className="min-w-0">
                      <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                        Terms of Use
                      </h1>
                      <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">SANIBIZ MIS - Please read these terms carefully</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4 bg-blue-50 px-6 py-3 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0V6a2 2 0 012-2h4a2 2 0 012 2v1m-6 0h6m-6 0v12a2 2 0 002 2h4a2 2 0 002-2V7m-6 0V6" />
                      </svg>
                      <span className="text-blue-900 font-semibold">Effective Date:</span>
                    </div>
                    <span className="text-blue-700 font-medium">March 1, 2025</span>
                  </div>
                </div>
              </div>

              {/* Section 1: Acceptance of Terms */}
              <section id="acceptance" className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-start space-x-3 sm:space-x-4 mb-4 sm:mb-6">
                  <div className="bg-blue-100 text-blue-700 p-2 sm:p-3 rounded-lg sm:rounded-xl flex-shrink-0">
                    <span className="text-lg sm:text-2xl">✅</span>
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Acceptance of Terms</h2>
                    <p className="text-sm sm:text-base text-gray-600 mt-1">Your agreement to these terms</p>
                  </div>
                </div>
                <div className="ml-16 space-y-4">
                  <div className="bg-gray-50 p-6 rounded-xl border-l-4 border-green-500">
                    <p className="text-gray-700 leading-relaxed">
                      By accessing or using SANIBIZ MIS (the "Application"), you confirm that you are at least
                      <span className="font-semibold text-green-700"> 18 years old</span> and have the legal capacity to enter into a binding
                      agreement. If you are using the Application on behalf of a business
                      entity, you represent that you have the authority to bind the entity to
                      these Terms.
                    </p>
                  </div>
                </div>
              </section>

              {/* Section 2: Use of the Application */}
              <section id="use-application" className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-start space-x-3 sm:space-x-4 mb-4 sm:mb-6">
                  <div className="bg-green-100 text-green-700 p-2 sm:p-3 rounded-lg sm:rounded-xl flex-shrink-0">
                    <span className="text-lg sm:text-2xl">⚙️</span>
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Use of the Application</h2>
                    <p className="text-sm sm:text-base text-gray-600 mt-1">Guidelines for proper application usage</p>
                  </div>
                </div>
                <div className="ml-16 space-y-4">
                  <div className="grid gap-4">
                    <div className="flex items-start space-x-3 sm:space-x-4 bg-blue-50 p-3 sm:p-4 rounded-lg">
                      <div className="bg-blue-200 p-1.5 sm:p-2 rounded-full flex-shrink-0">
                        <span className="text-blue-800 text-sm sm:text-base">🏢</span>
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-semibold text-blue-900 text-sm sm:text-base">Business Use</h4>
                        <p className="text-gray-700 text-xs sm:text-sm mt-1">
                          The Application is intended for use by registered business entities
                          in the tiles and sanitary products industry, with full customization available for your specific business requirements.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3 sm:space-x-4 bg-purple-50 p-3 sm:p-4 rounded-lg">
                      <div className="bg-purple-200 p-1.5 sm:p-2 rounded-full flex-shrink-0">
                        <span className="text-purple-800 text-sm sm:text-base">📋</span>
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-semibold text-purple-900 text-sm sm:text-base">Lawful Use</h4>
                        <p className="text-gray-700 text-xs sm:text-sm mt-1">
                          You agree to use the Application only for lawful purposes and in
                          compliance with all applicable laws and regulations.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3 sm:space-x-4 bg-red-50 p-3 sm:p-4 rounded-lg">
                      <div className="bg-red-200 p-1.5 sm:p-2 rounded-full flex-shrink-0">
                        <span className="text-red-800 text-sm sm:text-base">🚫</span>
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-semibold text-red-900 text-sm sm:text-base">Prohibited Activities</h4>
                        <p className="text-gray-700 text-xs sm:text-sm mt-1">
                          You must not misuse, interfere with, or disrupt the integrity of the
                          Application or attempt to gain unauthorized access.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Section 3: Account Registration */}
              <section id="account" className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-start space-x-3 sm:space-x-4 mb-4 sm:mb-6">
                  <div className="bg-orange-100 text-orange-700 p-2 sm:p-3 rounded-lg sm:rounded-xl flex-shrink-0">
                    <span className="text-lg sm:text-2xl">👤</span>
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Account Registration</h2>
                    <p className="text-sm sm:text-base text-gray-600 mt-1">Account security and responsibilities</p>
                  </div>
                </div>
                <div className="ml-0 sm:ml-8 lg:ml-16 space-y-4">
                  <div className="bg-orange-50 p-4 sm:p-6 rounded-lg sm:rounded-xl">
                    <ul className="space-y-2 sm:space-y-3">
                      <li className="flex items-start space-x-2 sm:space-x-3">
                        <div className="bg-orange-200 p-1 rounded-full mt-0.5 sm:mt-1 flex-shrink-0">
                          <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-orange-800" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <span className="text-gray-700 text-xs sm:text-sm">
                          To access certain features, you may need to create an account with proper authorization from your company administrator.
                        </span>
                      </li>
                      <li className="flex items-start space-x-2 sm:space-x-3">
                        <div className="bg-orange-200 p-1 rounded-full mt-0.5 sm:mt-1 flex-shrink-0">
                          <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-orange-800" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <span className="text-gray-700 text-xs sm:text-sm">
                          You are responsible for maintaining the confidentiality of your
                          account credentials and all activities under your account.
                        </span>
                      </li>
                      <li className="flex items-start space-x-2 sm:space-x-3">
                        <div className="bg-orange-200 p-1 rounded-full mt-0.5 sm:mt-1 flex-shrink-0">
                          <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-orange-800" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <span className="text-gray-700 text-xs sm:text-sm">
                          We reserve the right to suspend or terminate your account if we
                          suspect unauthorized or fraudulent activity.
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Section 4: Contact Information */}
              <section id="contact" className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-start space-x-3 sm:space-x-4 mb-4 sm:mb-6">
                  <div className="bg-purple-100 text-purple-700 p-2 sm:p-3 rounded-lg sm:rounded-xl flex-shrink-0">
                    <span className="text-lg sm:text-2xl">📞</span>
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Contact Information</h2>
                    <p className="text-sm sm:text-base text-gray-600 mt-1">How to reach us</p>
                  </div>
                </div>
                <div className="ml-0 sm:ml-8 lg:ml-16">
                  <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-4 sm:p-6 rounded-lg sm:rounded-xl border border-blue-100">
                    <p className="text-gray-700 mb-3 sm:mb-4 font-medium text-sm sm:text-base">For any questions regarding these Terms:</p>
                    <div className="space-y-3 sm:space-y-4">
                      <div className="flex items-start space-x-2 sm:space-x-3">
                        <div className="bg-blue-100 p-1.5 sm:p-2 rounded-lg flex-shrink-0">
                          <span className="text-base sm:text-xl">🏢</span>
                        </div>
                        <div className="text-gray-800 min-w-0">
                          <p className="font-semibold text-sm sm:text-base">Star Solutions</p>
                          <p className="text-xs sm:text-sm text-gray-600">
                            84, Bylane No 6, Borthakur Mill Road,<br/>
                            Ulubari, Guwahati - 781007,<br/>
                            Assam, India
                          </p>
                        </div>
                      </div>
                      <a 
                        href="mailto:info@starsolutions.biz" 
                        className="flex items-center space-x-2 sm:space-x-3 bg-white/80 p-3 sm:p-4 rounded-lg hover:bg-white transition-colors shadow-sm border border-blue-200"
                      >
                        <div className="bg-blue-100 p-1.5 sm:p-2 rounded-lg flex-shrink-0">
                          <span className="text-base sm:text-xl">📧</span>
                        </div>
                        <span className="font-medium underline text-blue-600 text-sm sm:text-base break-all">info@starsolutions.biz</span>
                      </a>
                      <a 
                        href="tel:+918133047257" 
                        className="flex items-center space-x-2 sm:space-x-3 bg-white/80 p-3 sm:p-4 rounded-lg hover:bg-white transition-colors shadow-sm border border-blue-200"
                      >
                        <div className="bg-blue-100 p-2 rounded-lg">
                          <span className="text-xl">📱</span>
                        </div>
                        <span className="font-medium text-gray-800">+91 8133047257</span>
                      </a>
                    </div>
                  </div>
                </div>
              </section>

              {/* Final Acknowledgement */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-dashed border-gray-300 text-center">
                <div className="max-w-2xl mx-auto">
                  <div className="bg-yellow-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                    <span className="text-2xl">⚖️</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Legal Acknowledgement</h3>
                  <p className="text-gray-700 italic">
                    By using SANIBIZ MIS, you acknowledge that you have read,
                    understood, and agreed to these Terms of Use.
                  </p>
                </div>
              </div>

              {/* Navigation Footer */}
              <div className="flex justify-between items-center bg-white rounded-xl p-6 shadow-lg border border-gray-200">
                <Link
                  to="/"
                  className="flex items-center space-x-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  <span>Back to Home</span>
                </Link>
                <Link
                  to="/privacy-policy"
                  className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <span>Privacy Policy</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TermsOfUse;
